import { ApiErrors } from "../backend/api-errors/errors";
import { Investment } from "../backend/generated/investment-model";
import { PaymentMethod } from "../backend/generated/payment-method-model";
import {
  APPROVE_USER,
  CREATE_INVESTMENT,
  CREATE_PAYMENT_METHOD,
  CREATE_SALE,
  DELETE_PAYMENT_METHOD,
  GENERATE_UPLOAD_SIGNED_URL,
  GET_CURRENT_USER,
  GET_CURRENT_USER_INVESTMENTS,
  GET_IDNOW_USER,
  GET_INVESTMENTS,
  GET_SALES,
  GET_USERS,
  LOGIN_USER,
  REGISTER_USER,
  SIGN_DOCUMENT_USER,
  START_IDNOW_USER,
  UPDATE_INVESTMENT,
  UPDATE_USER,
} from "../backend/graphql/queries";
import { STORE_INVESTMENTS_ACTION, STORE_UPDATE_INVESTMENT_ACTION } from "../store/investment/actions";
import { InvestmentManageable } from "../store/investment/types";
import { STORE_SALES_ACTION } from "../store/sale/actions";
import { SaleManageable } from "../store/sale/types";
import {
  LOGIN_ACTION,
  LOGOUT_ACTION,
  STORE_CURRENT_USER_ACTION,
} from "../store/user/actions";
import { UserManageable } from "../store/user/types";

export enum QueryType {
  Query,
  LazyQuery,
  Mutation
}

export interface QueryDescriptor {
  fetchPolicy?: string;
  input?: (storageData: any) => any;
  onError?: (error: ApiErrors, dispatch: any) => void;
  onSuccess?: (data: any, dispatch: any, storageData: any) => void;
  pollInterval?: number;
  query: any;
  skip?: (storageData: any) => boolean;
  type: QueryType;
}

/* QUERIES */

export const GetUsers = ({ onSuccess }: any): QueryDescriptor => {
  const query = GET_USERS;
  return {
    type: QueryType.Query,
    query: query.content,
    pollInterval: 25000,
    onSuccess: (data: any) => {
      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
};

export const GetCurrentUser = ({ onSuccess }: any): QueryDescriptor => {
  const query = GET_CURRENT_USER;
  return {
    type: QueryType.Query,
    query: query.content,
    onError: (
      _: ApiErrors, dispatch: any
    ) => {
      dispatch({ type: LOGOUT_ACTION });
    },
    onSuccess: (
      data: any, dispatch: any
    ) => {
      const userDetails = new UserManageable(data[query.name]);

      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: userDetails,
      });

      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
    pollInterval: 25000,
  };
}

export const GetCurrentUserInvestments = ({ onSuccess }: any): QueryDescriptor => {
  const query = GET_CURRENT_USER_INVESTMENTS;
  return {
    type: QueryType.Query,
    query: query.content,
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: STORE_INVESTMENTS_ACTION,
        payload: { investments: data[query.name].map((investment: Investment) => new InvestmentManageable(investment)) },
      });

      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
    pollInterval: 25000,
  };
}

export const GetIdNowUser = ({
  input,
  onSuccess,
}: any): QueryDescriptor => {
  const query = GET_IDNOW_USER;
  return {
    type: QueryType.Query,
    input: () => input,
    query: query.content,
    onSuccess: (data: any) => {
      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
}

export const GetInvestments = ({
  input,
  onSuccess,
}: any): QueryDescriptor => {
  const query = GET_INVESTMENTS;
  return {
    fetchPolicy: 'network-only',
    type: QueryType.Query,
    input: () => input,
    query: query.content,
    onSuccess: (data: any) => {
      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
}

export const GetSales = ({ onSuccess }: any): QueryDescriptor => {
  const query = GET_SALES;
  return {
    type: QueryType.LazyQuery,
    query: query.content,
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: STORE_SALES_ACTION,
        payload: { sales: data[query.name].map((sale: any) => new SaleManageable(sale)) },
      });

      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
}

/* LAZY QUERIES */

export const GetCurrentUserLazy = ({
  onSuccess,
  onError,
}: any): QueryDescriptor => {
  const query = GET_CURRENT_USER;
  return {
    fetchPolicy: 'network-only',
    type: QueryType.LazyQuery,
    query: query.content,
    onSuccess: (
      data: any, dispatch: any
    ) => {
      let userDetails = new UserManageable(data[query.name]);

      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: userDetails,
      });

      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
    onError: (error: any) => {
      if (onError) {
        onError(error);
      }
    },
  };
}

/* MUTATIONS */

export const ApproveUser = ({ onSuccess }: any): QueryDescriptor => {
  const query = APPROVE_USER;
  return {
    type: QueryType.Mutation,
    query: query.content,
    onSuccess: (data: any) => {
      onSuccess(data[query.name]);
    },
  };
}

export const CreateInvestment = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  const query = CREATE_INVESTMENT;
  return {
    type: QueryType.Mutation,
    query: query.content,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any) => {
      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
}

export const CreatePaymentMethod = ({ onSuccess }: any): QueryDescriptor => {
  const query = CREATE_PAYMENT_METHOD;
  return {
    type: QueryType.Mutation,
    query: query.content,
    onSuccess: (
      data: any, dispatch: any, storageData: any
    ) => {
      let userDetails = new UserManageable(storageData);
      if (!userDetails.paymentMethods) {
        userDetails.paymentMethods = [];
      }
      userDetails.paymentMethods.push(data[query.name]);

      if (userDetails.paymentMethods.length === 1) {
        userDetails.defaultPaymentMethod = data[query.name].id;
      }

      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: userDetails,
      });

      onSuccess(data[query.name]);
    },
  };
}

export const CreateSale = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  const query = CREATE_SALE;
  return {
    type: QueryType.Mutation,
    query: query.content,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (data: any) => {
      onSuccess(data[query.name]);
    },
  };
}

export const DeletePaymentMethod = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  const query = DELETE_PAYMENT_METHOD;
  return {
    type: QueryType.Mutation,
    query: query.content,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (
      data: any, dispatch: any, storageData: any
    ) => {
      let userDetails = new UserManageable(storageData);
      if (userDetails.paymentMethods) {
        userDetails.paymentMethods = userDetails.paymentMethods.filter((paymentMethod: PaymentMethod) => paymentMethod.id !== data[query.name].id);
      }

      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: userDetails,
      });

      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
}

export const GenerateUploadSignedURL = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  const query = GENERATE_UPLOAD_SIGNED_URL;
  return {
    type: QueryType.Mutation,
    query: query.content,
    onError: (error: ApiErrors) => {
      if (onError) {
        onError(error);
      }
    },
    onSuccess: (data: any) => {
      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
}

export const LoginUser = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  const query = LOGIN_USER;
  return {
    type: QueryType.Mutation,
    query: query.content,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: LOGIN_ACTION,
        payload: { token: data[query.name] },
      });

      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
}

export const RegisterUser = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  const query = REGISTER_USER;
  return {
    type: QueryType.Mutation,
    query: query.content,
    onError: (error: ApiErrors) => {
      onError(error);
    },
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: LOGIN_ACTION,
        payload: { token: data[query.name] },
      });

      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
}

export const StartIdNowUser = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  const query = START_IDNOW_USER;
  return {
    type: QueryType.Mutation,
    query: query.content,
    onError: (error: ApiErrors) => {
      if (onError) {
        onError(error);
      }
    },
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: new UserManageable(data[query.name]),
      });

      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
}

export const SignDocumentUser = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  const query = SIGN_DOCUMENT_USER;
  return {
    type: QueryType.Mutation,
    query: query.content,
    onError: (error: ApiErrors) => {
      if (onError) {
        onError(error);
      }
    },
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: new UserManageable(data[query.name]),
      });

      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
}

export const UpdateInvestment = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  const query = UPDATE_INVESTMENT;
  return {
    type: QueryType.Mutation,
    query: query.content,
    onError: (error: ApiErrors) => {
      if (onError) {
        onError(error);
      }
    },
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: STORE_UPDATE_INVESTMENT_ACTION,
        payload: new InvestmentManageable(data[query.name]),
      });

      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
}

export const UpdateUser = ({
  onError,
  onSuccess,
}: any): QueryDescriptor => {
  const query = UPDATE_USER;
  return {
    type: QueryType.Mutation,
    query: query.content,
    onError: (error: ApiErrors) => {
      if (onError) {
        onError(error);
      }
    },
    onSuccess: (
      data: any, dispatch: any
    ) => {
      dispatch({
        type: STORE_CURRENT_USER_ACTION,
        payload: new UserManageable(data[query.name]),
      });

      if (onSuccess) {
        onSuccess(data[query.name]);
      }
    },
  };
}
